import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, shareReplay } from 'rxjs';

import { environment } from '../../environments/environment';

export interface PlatformSettings {
  commission_rate: number;
  trial_days: number;
  // Días de gracia por defecto tras vencer el pago antes de bloquear el
  // acceso -- solo el valor de partida para negocios nuevos/sin
  // personalizar (cada suscripción conserva el suyo propio una vez que se
  // le asigna uno explícito en "Personalizar suscripción").
  grace_days: number;
}

export interface CommissionChangeEvent {
  id: number;
  previous_rate: number;
  new_rate: number;
  created_at: string;
  // Solo negocios realmente afectados por una selección explícita o por la
  // normalización de un contrato legado sin tasa.
  affected_businesses: Array<{ id: number; name: string; commission_rate: number | null }>;
  changed_by: { user_id: number; name: string; email: string } | null;
}

export interface CommissionContract {
  id: number;
  name: string;
  commission_rate: number | null;
}

@Injectable({ providedIn: 'root' })
export class PlatformSettingsService {
  private readonly url = `${environment.apiUrl}/platform-settings`;
  private cache$: Observable<{ settings: PlatformSettings }> | null = null;

  constructor(private readonly http: HttpClient) {}

  get(): Observable<{ settings: PlatformSettings }> {
    if (!this.cache$) {
      this.cache$ = this.http.get<{ settings: PlatformSettings }>(this.url).pipe(shareReplay(1));
    }
    return this.cache$;
  }

  update(data: Partial<PlatformSettings> & { affected_business_ids?: number[] }): Observable<{ settings: PlatformSettings; msg: string }> {
    this.cache$ = null;
    return this.http.patch<{ settings: PlatformSettings; msg: string }>(this.url, data);
  }

  getCommissionHistory(): Observable<{ changes: CommissionChangeEvent[] }> {
    return this.http.get<{ changes: CommissionChangeEvent[] }>(`${this.url}/commission-history`);
  }

  getCommissionContracts(search = ''): Observable<{ businesses: CommissionContract[] }> {
    const suffix = search.trim() ? `?search=${encodeURIComponent(search.trim())}` : '';
    return this.http.get<{ businesses: CommissionContract[] }>(`${this.url}/commission-contracts${suffix}`);
  }
}
